import { FiExternalLink } from "react-icons/fi";
import type { RemoteVendorProduct } from "../../../../features/interfaces";

/** Column definitions for remote vendor products (presentation concern only). */
export function getRemoteProductColumns() {
  return [
    {
      id: "product",
      header: "Product",
      accessorKey: "title",
      cell: (info: any) => {
        const product = info.row.original as RemoteVendorProduct;
        return (
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-9 w-9 shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-50">
              {product.image_url ? (
                <img
                  src={product.image_url}
                  alt={product.title}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-[10px] text-gray-400">
                  N/A
                </div>
              )}
            </div>
            <div className="min-w-0">
              <p className="truncate font-semibold text-gray-800">
                {product.title}
              </p>
              <p className="truncate text-xs text-gray-500 font-mono">
                ID: {product.id}
              </p>
            </div>
          </div>
        );
      },
      size: 220,
      meta: { align: "left" as const },
    },
    {
      id: "description",
      header: "Description",
      accessorKey: "description",
      cell: (info: any) => {
        const value = (info.getValue() as string) || "";
        return (
          <p
            className="text-sm text-gray-600 line-clamp-2 max-w-[240px]"
            title={value || undefined}
          >
            {value || <span className="text-gray-400">—</span>}
          </p>
        );
      },
      size: 220,
      meta: { align: "left" as const },
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "status",
      cell: (info: any) => (
        <span className="text-sm text-gray-600 capitalize">
          {info.getValue() || "—"}
        </span>
      ),
      meta: { align: "left" as const },
    },
    {
      id: "variant_id",
      header: "Variant ID",
      cell: ({ row }: any) => {
        const product = row.original as RemoteVendorProduct;
        const variants = product.variants || [];
        if (variants.length === 0) {
          return <span className="text-gray-400 text-xs">—</span>;
        }
        return (
          <div className="space-y-1 max-w-[160px]">
            {variants.map((variant) => (
              <p
                key={variant.id}
                className="text-xs font-mono text-gray-600 truncate"
                title={variant.title}
              >
                {variant.id}
              </p>
            ))}
          </div>
        );
      },
      size: 150,
      meta: { align: "left" as const },
    },
    {
      id: "stock",
      header: "Stock",
      cell: ({ row }: any) => {
        const product = row.original as RemoteVendorProduct;
        const variants = product.variants || [];
        if (variants.length === 0) {
          return <span className="text-gray-400 text-xs">—</span>;
        }
        const total = variants.reduce(
          (sum, variant) => sum + (variant.inventory_quantity ?? 0),
          0
        );
        return (
          <div className="text-sm text-gray-700">
            <span className="font-semibold">{total}</span>
            {variants.length > 1 ? (
              <div className="mt-0.5 space-y-0.5">
                {variants.map((variant) => (
                  <p key={variant.id} className="text-[11px] text-gray-400">
                    {variant.title}: {variant.inventory_quantity ?? 0}
                  </p>
                ))}
              </div>
            ) : null}
          </div>
        );
      },
      meta: { align: "right" as const },
    },
    {
      id: "price",
      header: "Price",
      accessorFn: (row: RemoteVendorProduct) => row.variants?.[0]?.price ?? null,
      cell: (info: any) => {
        const value = info.getValue();
        return (
          <span className="text-sm text-gray-700 font-medium">
            {value != null ? `$${value}` : "—"}
          </span>
        );
      },
      meta: { align: "right" as const },
    },
    {
      id: "url",
      header: "Link",
      cell: ({ row }: any) => {
        const product = row.original as RemoteVendorProduct;
        if (!product.url) {
          return <span className="text-gray-400 text-xs">—</span>;
        }
        return (
          <a
            href={product.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#3a5d95] hover:underline"
          >
            Open <FiExternalLink size={12} />
          </a>
        );
      },
      meta: { align: "center" as const },
    },
  ];
}
